import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Card } from 'react-bootstrap';
import { FormikInput, FormikSelect, ButtonTooltip, Icon } from '../../../components';
import { debounceIndexAdminCompaniesRequest } from '../../../requests/adminCompanies';
import { debounceIndexBranchOfficesRequest } from '../../../requests/adminBranchOffices';

const originOptions = [
  { label: 'Biotime ZKTeco', value: 'biotime' },
  { label: 'Biotime (PeopleWork)', value: 'pw_biotime' }
];

const resultCompanyFetchData = response => {
  const result = response.data.data;
  const tempArray = result?.map(element => ({
    label: `${element.national_identification} - ${element.business_name}`,
    value: Number(element.id),
    id: Number(element.id),
    api_key: element.api_key,
    secret_key: element.secret_key
  }));
  return tempArray || [];
};

const ControlClockForm = ({
  submitVariant,
  isSubmitting,
  setFieldValue,
  errors,
  touched,
  action,
  values
}) => {
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState([]);
  const [branchOfficesByCompany, setBranchOfficesByCompany] = useState({});
  const fetchedCompaniesRef = useRef(new Set());

  const { device: vDevice } = values || {};
  const { origin: vOrigin, companies: vCompanies = [] } = vDevice || {};

  const fetchCompanies = useCallback(() => {
    debounceIndexAdminCompaniesRequest({
      dispatch,
      params: {
        order_by_attribute: 'business_name',
        paginate: false
      },
      successCallback: result => {
        setCompanies(resultCompanyFetchData(result));
      }
    });
  }, [dispatch]);

  const fetchBranchOfficesForCompany = useCallback(
    companyId => {
      const numericId = Number(companyId);
      if (!numericId || fetchedCompaniesRef.current.has(numericId)) return;
      fetchedCompaniesRef.current.add(numericId);

      debounceIndexBranchOfficesRequest({
        dispatch,
        params: {
          actives: true,
          sort_column: 'name',
          company_id: numericId,
          paginate: false
        },
        successCallback: result => {
          const rawBranches = result?.data?.data || [];
          const branches = rawBranches.map(b => ({
            value: Number(b.value || b.id),
            label: b.label || b.name,
            id: Number(b.id || b.value),
            name: b.name || b.label
          }));
          setBranchOfficesByCompany(prev => ({
            ...prev,
            [numericId]: branches
          }));
        },
        failureCallback: () => {
          fetchedCompaniesRef.current.delete(numericId);
        }
      });
    },
    [dispatch]
  );

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    vCompanies.forEach(comp => {
      if (comp.company_id) {
        fetchBranchOfficesForCompany(comp.company_id);
      }
    });
  }, [vCompanies, fetchBranchOfficesForCompany]);

  const handleCompanyChange = (index, data) => {
    const updated = [...vCompanies];
    const compId = data ? Number(data.value) : '';
    let businessName = '';
    if (data) {
      const parts = data.label ? data.label.split(' - ') : [];
      businessName = parts.length > 1 ? parts.slice(1).join(' - ') : (data.label || '');
    }
    const apiKey = data ? data.api_key : '';
    const secretKey = data ? data.secret_key : '';

    updated[index] = {
      ...updated[index],
      company_id: compId,
      business_name: businessName,
      api_key: apiKey,
      secret_key: secretKey,
      branch_office_id: '',
      branch_office_name: ''
    };

    setFieldValue('device.companies', updated);
    if (compId) {
      fetchBranchOfficesForCompany(compId);
    }
  };

  const handleBranchOfficeChange = (index, data) => {
    const updated = [...vCompanies];
    const branchId = data ? Number(data.value || data.id) : '';
    const branchName = data ? (data.name || data.label) : '';

    updated[index] = {
      ...updated[index],
      branch_office_id: branchId,
      branch_office_name: branchName
    };
    setFieldValue('device.companies', updated);
  };

  const handleAddCompany = () => {
    const updated = [
      ...vCompanies,
      {
        company_id: '',
        business_name: '',
        api_key: '',
        secret_key: '',
        branch_office_id: '',
        branch_office_name: ''
      }
    ];
    setFieldValue('device.companies', updated);
  };

  const handleRemoveCompany = index => {
    if (vCompanies.length <= 1) return;
    const updated = vCompanies.filter((_, i) => i !== index);
    setFieldValue('device.companies', updated);
  };

  const companiesError = getIn(errors, 'device.companies');

  return (
    <Form>
      <Row>
        <Col md={4}>
          <Field name="device[sn]">
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                disabled={action !== 'new'}
                label="Serial"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={4}>
          <Field name="device[alias]">
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                label="Alias"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={4}>
          <Field name="device[ip_address]">
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                disabled={action !== 'new'}
                label="Dirección IP"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={4}>
          <Field name="device[heartbeat]">
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                label="Intervalo de Solicitud"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={4}>
          <Field name="device[origin]">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                isDisabled={action !== 'new'}
                label="Origen del dispositivo"
                placeholder="Seleccionar Origen del dispositivo"
                options={originOptions}
                defaultValue={vOrigin}
                value={vOrigin}
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>

      <div className="mt-4 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h5 className="mb-0 text-uppercase font-weight-bold">
              Empresas y Lugares de Prestación de Servicios
            </h5>
            <small className="text-muted">
              Puedes asociar una o más empresas para que los colaboradores de diferentes empresas marquen en este mismo reloj.
            </small>
          </div>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={handleAddCompany}
            className="d-flex align-items-center"
          >
            <Icon icon="plus" className="mr-1" />
            Asociar otra Empresa
          </Button>
        </div>

        {typeof companiesError === 'string' && (
          <div className="alert alert-danger py-2 mb-3">{companiesError}</div>
        )}

        {vCompanies.map((companyItem, index) => {
          const compId = companyItem.company_id ? Number(companyItem.company_id) : '';
          const branchId = companyItem.branch_office_id ? Number(companyItem.branch_office_id) : '';

          let rowCompanyOptions = [...companies];
          if (compId && !rowCompanyOptions.some(c => Number(c.value) === compId)) {
            const currentLabel = companyItem.business_name || `Empresa (${compId})`;
            rowCompanyOptions = [
              {
                value: compId,
                label: currentLabel,
                id: compId,
                api_key: companyItem.api_key || '',
                secret_key: companyItem.secret_key || ''
              },
              ...rowCompanyOptions
            ];
          }

          const cachedBranches = compId ? branchOfficesByCompany[compId] || [] : [];
          let rowBranchOptions = [...cachedBranches];
          if (branchId && !rowBranchOptions.some(b => Number(b.value || b.id) === branchId)) {
            const currentBranchName =
              companyItem.branch_office_name || `Lugar (${branchId})`;
            rowBranchOptions = [
              {
                value: branchId,
                label: currentBranchName,
                id: branchId,
                name: currentBranchName
              },
              ...rowBranchOptions
            ];
          }

          const companyFieldName = `device.companies[${index}].company_id`;
          const branchFieldName = `device.companies[${index}].branch_office_id`;
          const rowKey = compId ? `comp-row-${compId}` : `comp-row-pos-${String(index)}`;

          return (
            <Card key={rowKey} className="mb-3 border-light shadow-sm">
              <Card.Body className="p-3">
                <Row className="align-items-center">
                  <Col md={5}>
                    <Field name={companyFieldName}>
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          abbr
                          label={`Empresa ${vCompanies.length > 1 ? `#${index + 1}` : ''}`}
                          placeholder="Seleccionar Empresa"
                          options={rowCompanyOptions}
                          defaultValue={compId}
                          value={compId}
                          onChange={data => handleCompanyChange(index, data)}
                          error={getIn(errors, companyFieldName)}
                          touched={getIn(touched, companyFieldName)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col md={vCompanies.length > 1 ? 6 : 7}>
                    <Field name={branchFieldName}>
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          abbr
                          label="Lugar de Prestación de Servicios"
                          placeholder={
                            compId
                              ? 'Seleccionar Lugar de Prestación de Servicios'
                              : 'Primero selecciona una empresa'
                          }
                          options={rowBranchOptions}
                          defaultValue={branchId}
                          value={branchId}
                          isDisabled={!compId}
                          onChange={data => handleBranchOfficeChange(index, data)}
                          error={getIn(errors, branchFieldName)}
                          touched={getIn(touched, branchFieldName)}
                        />
                      )}
                    </Field>
                  </Col>
                  {vCompanies.length > 1 && (
                    <Col md={1} className="d-flex justify-content-center align-items-center mt-3">
                      <ButtonTooltip
                        onClick={() => handleRemoveCompany(index)}
                        variant="circle-danger"
                        className="btn-circle"
                        size="sm"
                        text="Eliminar Empresa"
                      >
                        <Icon icon="trash" />
                      </ButtonTooltip>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      <Row className="d-flex justify-content-end mt-5 mt-md-3 mb-3">
        <Col md={3} xl={2}>
          <Button type="submit" disabled={isSubmitting} variant={submitVariant} block>
            {action === 'new' ? 'Crear' : 'Actualizar'}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const setInitialValues = props => {
  const { device = {} } = props;
  let rawList = [];

  if (device.companies && Array.isArray(device.companies) && device.companies.length > 0) {
    rawList = device.companies;
  } else if (device.company_id || device.company?.id) {
    rawList = [
      {
        company_id: device.company_id || device.company?.id,
        business_name: device.company?.business_name || '',
        api_key: device.company?.api_key || '',
        secret_key: device.company?.secret_key || '',
        branch_office_id: device.branch_office_id || device.branch_office?.id || '',
        branch_office_name: device.branch_office?.name || ''
      }
    ];
  }

  let companiesList = [];
  if (rawList.length > 0) {
    companiesList = rawList.map(c => {
      let compId = '';
      if (c.company_id) {
        compId = Number(c.company_id);
      } else if (c.id) {
        compId = Number(c.id);
      }
      let branchId = '';
      if (c.branch_office_id) {
        branchId = Number(c.branch_office_id);
      } else if (c.branch_office?.id) {
        branchId = Number(c.branch_office.id);
      }
      const branchName = c.branch_office_name || c.branch_office?.name || '';

      return {
        company_id: compId,
        business_name: c.business_name || '',
        api_key: c.api_key || '',
        secret_key: c.secret_key || '',
        branch_office_id: branchId,
        branch_office_name: branchName
      };
    });
  } else {
    companiesList = [
      {
        company_id: '',
        business_name: '',
        api_key: '',
        secret_key: '',
        branch_office_id: '',
        branch_office_name: ''
      }
    ];
  }

  const primaryComp = companiesList[0] || {};

  return {
    device: {
      sn: device.sn || '',
      alias: device.alias || '',
      ip_address: device.ip_address || '',
      heartbeat: device.heartbeat || '',
      origin: device.origin || 'biotime',
      company_id: primaryComp.company_id || '',
      branch_office_id: primaryComp.branch_office_id || '',
      company: {
        id: primaryComp.company_id || '',
        business_name: primaryComp.business_name || '',
        api_key: primaryComp.api_key || '',
        secret_key: primaryComp.secret_key || ''
      },
      branch_office: {
        id: primaryComp.branch_office_id || '',
        name: primaryComp.branch_office_name || ''
      },
      companies: companiesList
    }
  };
};

const validationSchema = Yup.object().shape({
  device: Yup.object().shape({
    sn: Yup.string().required('Debes ingresar un serial'),
    alias: Yup.string().required('Debes ingresar un alias'),
    ip_address: Yup.string()
      .required('Debes ingresar una Dirección Ip')
      .matches(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/, 'Ingresa una dirección IP válida'),
    heartbeat: Yup.number()
      .required('Debes ingresar un Intervalo de Solicitud')
      .nullable()
      .typeError('Debes ingresar un número')
      .min(0, 'Debe ser mayor o igual a 0')
      .max(99, 'Deben ser menos que 2 caracteres'),
    companies: Yup.array()
      .of(
        Yup.object().shape({
          company_id: Yup.mixed().required('Debes seleccionar una Empresa'),
          branch_office_id: Yup.mixed().required('Debes seleccionar un Lugar de Prestación de Servicios')
        })
      )
      .min(1, 'Debes asociar al menos una Empresa')
      .test('unique-companies', 'No puedes seleccionar la misma empresa más de una vez', list => {
        if (!list) return true;
        const ids = list.map(c => c && c.company_id).filter(Boolean);
        return new Set(ids).size === ids.length;
      })
  })
});

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  const { device = {} } = values;

  const formattedCompanies = (device.companies || []).map(c => ({
    id: c.company_id ? Number(c.company_id) : '',
    business_name: c.business_name,
    api_key: c.api_key,
    secret_key: c.secret_key,
    branch_office: {
      id: c.branch_office_id ? Number(c.branch_office_id) : '',
      name: c.branch_office_name
    }
  }));

  const primaryComp = formattedCompanies[0] || {};

  const payload = {
    device: {
      sn: device.sn,
      alias: device.alias,
      ip_address: device.ip_address,
      heartbeat: device.heartbeat,
      origin: device.origin,
      companies: formattedCompanies,
      company: {
        id: primaryComp.id,
        business_name: primaryComp.business_name,
        api_key: primaryComp.api_key,
        secret_key: primaryComp.secret_key
      },
      branch_office: primaryComp.branch_office || {}
    }
  };

  formRequest(payload);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(ControlClockForm);
